<script setup lang="ts">
  import type { IBestBuyBanner } from '@/types/BestBuyBanners'
  import { cva } from 'class-variance-authority'
  import { ONE_WAY } from '@/components/clp/constants'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'

  const props = defineProps<{ data: IBestBuyBanner }>()

  const runtimeConfig = useRuntimeConfig()
  const bgImageSrc = computed(
    () => `${runtimeConfig.public.btWebAssets}/Best_buy_banner_pictures_350_220/${props.data.dest.toLowerCase()}.webp`
  )

  const cardStyle = computed(() =>
    cva('w-full rounded-md bg-white p-4 shadow-bbb-slider md:absolute', {
      variants: {
        style: {
          active: 'z-5 absolute',
          nonActive: 'relative'
        }
      }
    })({
      style: 'nonActive'
    })
  )

  const image = useImage()

  const card = ref<HTMLElement | null>(null)
  const { pushBestBuyBannerDataLayer } = useIndexPageDataLayer()

  onElementVisibleAtThreshold(card, () => {
    pushBestBuyBannerDataLayer({
      event_action: 'impressions',
      oac: props.data.origin,
      dac: props.data.dest
    })
  })
</script>
<template>
  <div
    ref="card"
    tabindex="0"
    class="group relative my-4 transition-all duration-300 md:h-96"
    :aria-label="data.destCity"
  >
    <div :class="cardStyle">
      <h3 class="sr-only">{{ data.destCity }}</h3>
      <div class="relative">
        <div class="mb-4 max-h-50 w-full overflow-hidden rounded-sm">
          <NuxtImg
            :src="bgImageSrc"
            :placeholder="image('/placeholder-img.png', { h: 200, f: 'webp', q: 70 })"
            alt=""
            class="block h-50 w-full object-cover transition-all duration-300 group-focus-within:scale-110 group-hover:scale-110"
            format="webp"
            loading="lazy"
          />
        </div>
        <div
          v-if="data.labels"
          class="absolute left-2 top-2 flex flex-col gap-1 text-subtitle-2"
        >
          <span
            v-for="label in data.labels"
            :key="label.id"
            class="rounded-md bg-bt-green px-2 py-0.5"
          >
            {{ label.label }}
          </span>
        </div>
      </div>
      <p class="mb-1 text-bt-blue-dark text-subtitle-1">{{ data.originCity }} -</p>
      <div class="mb-1 text-bt-blue-dark text-h5">{{ data.destCity }}</div>
      <div class="mb-3 flex items-center gap-2 text-secondary">
        <span class="rounded-sm border border-border px-2 py-0.5 text-subtitle-2">{{ data.dest }}</span>
        <span class="text-body-2">{{ data.destCountry }}</span>
      </div>
      <div class="flex items-baseline justify-between text-bt-blue-dark">
        <p
          data-test="bbb-month"
          class="mb-0 text-body-2"
        >
          {{ data.month }}
        </p>
        <p class="mb-0">
          <span class="mr-1 text-body-3">{{ $t('clp.priceFrom') }}</span>
          <span
            data-test="bbb-price"
            class="text-h4"
          >
            {{ data.price }}€
          </span>
        </p>
      </div>
      <div
        class="transition-all duration-300 group-focus-within:h-16 group-focus-within:opacity-100 group-hover:h-16 group-hover:opacity-100 md:h-0 md:opacity-0"
      >
        <BestBuyBannerDestinationButton
          :data="data"
          :selected-direction="ONE_WAY"
          class="mt-4"
        />
      </div>
    </div>
  </div>
</template>
